import { CRM_CONTACT_FIELD_LABELS as L, createCrmContactCardTemplate } from '@/Containers/CrmSection/Components/Contacts'
import { copyableListView, renderDateTimeView, renderDateView, renderUsersView } from '@/Ship/Helpers/fieldViews'
import { formatPhone } from '@/Ship/Helpers/formatPhone'

import { CLIENT_CONTACT_COMMENT_REST_URL, CLIENT_CONTACT_REST_URL } from '../Contracts/IClient'

import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'
import type { IFieldConfig } from '@/Core/Contracts/UI/Form'

/** Лише перегляд: ставить сервер (аудит). */
const readOnly = (field: IFieldConfig): IFieldConfig => ({
    ...field,
    isEditable: false,
    inputProps: { ...(field.inputProps ?? {}), disabled: true },
})

/**
 * Поля, яких бек від форми не приймає: службові ставить він сам, `client`
 * задається лише при створенні — змінити власника контакту не можна
 * (`SPEC-008` §5.1). Не шлемо їх, щоб тіло запиту не обіцяло зміни, якої не
 * буде.
 */
const READ_ONLY_KEYS = [
    'id',
    'title',
    'fullName',
    'externalCode',
    'isRemoved',
    'createdAt',
    'createdBy',
    'updatedAt',
    'updatedBy',
] as const

const prepareContactPayload = ({
    values,
    formContext,
}: {
    values: Record<string, unknown>
    formContext?: { values?: Record<string, unknown> }
}): void => {
    READ_ONLY_KEYS.forEach((key) => delete values[key])

    if (typeof formContext?.values?.['@id'] === 'string') delete values['client']
}

/** Картка контакту клієнта: стрічка праворуч — коментарі контакту, вкладка «Листи». */
const ClientContactCardTemplate = createCrmContactCardTemplate({
    commentsRestUrl: CLIENT_CONTACT_COMMENT_REST_URL,
    lettersOwner: 'clientContact',
})

/**
 * Контакти клієнта (`SPEC-009` §4.4, §8.4–§8.5, `SPEC-008` §4.2).
 *
 * Власного реєстру немає — лише вкладка «Контакти» в картці клієнта, тож без
 * `pageUrl`. Створюють контакт тільки звідти: клієнт задається при створенні
 * й більше не змінюється, вибору клієнта у формі немає (`CREATED_FROM_PARENT`).
 *
 * Права — лише з беку (`SPEC-003` §1.3): читати — хто бачить клієнта,
 * змінювати й видаляти — `_permissions` контакту (= `update` клієнта).
 * Ролей ресурс не питає.
 *
 * Шухляда — 1100 px: поріг двох стовпців блоків — 960 px вмісту, а праворуч
 * ще стрічка коментарів (`SPEC-009` §8.4).
 */
export const ClientContactResource: IResourceConfig = {
    title: 'Контакти клієнта',
    fields: {
        name: {
            label: L.name,
            isRequired: true,
            inputProps: { maxLength: 150 },
        },
        lastName: {
            label: L.lastName,
            inputProps: { maxLength: 150 },
        },
        position: {
            label: L.position,
            inputProps: { maxLength: 150 },
        },
        comment: {
            label: L.comment,
            type: 'textarea',
            inputProps: { maxLength: 4096 },
        },
        email: {
            label: L.email,
            inputProps: { maxLength: 150 },
            view: copyableListView(),
        },
        phone: {
            label: L.phone,
            type: 'phone',
            // У базі самими цифрами — у картці «+38 (067) 123-45-67» з копіюванням
            view: copyableListView(formatPhone),
        },
        fullNameEnglish: {
            label: L.fullNameEnglish,
            inputProps: { maxLength: 150 },
        },
        // Дата без часу: у легасі тут стояв вибір дати з часом (`SPEC-008` §4.2)
        birthDay: {
            label: L.birthDay,
            type: 'date',
            view: renderDateView,
        },
        createdAt: readOnly({
            label: L.createdAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        createdBy: readOnly({
            label: L.createdBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
        updatedAt: readOnly({
            label: L.updatedAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        updatedBy: readOnly({
            label: L.updatedBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
    },
    form: {
        eventHandlers: {
            onBeforeSendSaveRequest: {
                ClientContactPayload: prepareContactPayload,
            },
        },
    },
    detailConfig: {
        modeConfig: {
            view: { template: ClientContactCardTemplate },
            edit: { template: ClientContactCardTemplate },
            create: { template: ClientContactCardTemplate },
        },
    },
    drawer: {
        width: 1100,
    },
    restUrl: CLIENT_CONTACT_REST_URL,
}
